import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFridgeId } from "utils/browser/localStorage";
import { logToSentry } from "utils/logger";
import { getShortenedFridgeId } from "utils/utils";

import ErrorModalImage from "assets/illustrations/errorModalImage.svg";
import { ModalContentWrapper } from "components/primitives/ModalContentWrapper";
import { ModalTitle } from "components/primitives/ModalTitle";
import { useCountdown } from "hooks/timers";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";

import { BasicModal } from "./BasicModal";
import { ModalSupportFooter } from "./ModalSupportFooter";

type ConnectionErrorModalProps = {
	appConnected: boolean;
	isSubscriptionBroken?: boolean;
};

export const ConnectionErrorModal = ({
	appConnected,
	isSubscriptionBroken,
}: ConnectionErrorModalProps) => {
	const fridgeId = getFridgeId();
	const shortenedFridgeId = getShortenedFridgeId(fridgeId);
	const [isOpen, setIsOpen] = useState(
		isSubscriptionBroken !== undefined && isSubscriptionBroken ? false : !appConnected,
	);
	const { countdown } = useCountdown(15);
	const { trackTabletErrorModal } = useMixpanelTrackers();
	const trackRef = useRef(trackTabletErrorModal);
	trackRef.current = trackTabletErrorModal;
	const wasOpenRef = useRef(false);

	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			wasOpenRef.current = true;
			trackRef.current("open");
			logToSentry(`Error modal displayed on fridge: ${fridgeId}`, "error");
		}
		return () => {
			if (wasOpenRef.current) {
				wasOpenRef.current = false;
				trackRef.current("close");
			}
		};
	}, [isOpen]);

	useEffect(() => {
		if (isSubscriptionBroken && countdown === 0) {
			setIsOpen(true);
		}
	}, [countdown, isSubscriptionBroken]);

	return (
		<>
			<BasicModal
				open={isOpen}
				fullHeight={true}
				closable={false}
				modalContent={<ErrorModalContent fridgeId={shortenedFridgeId} />}
			/>
		</>
	);
};

const ErrorModalContent = ({ fridgeId }: { fridgeId: string }) => {
	const { t } = useTranslation();
	return (
		<ModalContentWrapper>
			<ModalTitle variant={"promoHeader"}>{t("modals.error.title")}</ModalTitle>
			<Typography variant={"promoText"} mb={5}>
				{t("modals.error.body")}
			</Typography>
			<ErrorModalImage />
			<ModalSupportFooter fridgeId={fridgeId} />
		</ModalContentWrapper>
	);
};
